import { useEffect, useRef } from "react";
import css from "./TextLinkButton.module.scss";

interface TextLinkButtonProps {
    readonly text: string;
    readonly onClick: () => void;
    readonly ariaLabel?: string;
    readonly title?: string;
}

/**
 * A text-styled button that looks like a link. Safe to nest inside clickable
 * containers (e.g. ag-grid rows, cards)
 */
export const TextLinkButton: React.FC<TextLinkButtonProps> = ({
    text,
    onClick,
    ariaLabel,
    title
}) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    // Keep a ref to the latest onClick so the native listener stays bound
    // across renders without re-attaching on every onClick identity change.
    const onClickRef = useRef(onClick);
    onClickRef.current = onClick;

    useEffect(() => {
        const button = buttonRef.current;
        if (!button) {
            return;
        }
        const handleClick = (event: MouseEvent) => {
            event.stopPropagation();
            onClickRef.current();
        };
        button.addEventListener("click", handleClick);
        return () => button.removeEventListener("click", handleClick);
    }, []);

    return (
        <button
            ref={buttonRef}
            type="button"
            className={css["TextLinkButton"]}
            aria-label={ariaLabel}
            title={title}
        >
            {text}
        </button>
    );
};
